import { TEXT_LAYOUT_PARAGRAPHS, textLayoutAtLeast, type WrapZone } from '@lite-workspace/text-layout';
import { isBoxTextWrap, type BoxTextWrap } from '@lite-workspace/dto-fotoclass-templates';
import type { RenderBox, RenderRect, RenderTextBox } from './render-box';

type Point = readonly [number, number];

/**
 * Ceilings on a `textWrap` from saved model JSON before the engine sees it: the slot search costs lines × zones ×
 * contour points. The importer writes no more than this (indd-import `MAX_WRAP_CONTOUR_POINTS`); a longer contour is
 * thinned the same way, keeping every n-th point.
 */
export const TEXT_WRAP_MAX_CONTOUR_POINTS = 64;

/**
 * Largest offset a wrap keeps, canvas px: the importer's measure range (indd-import `MAX_MEASURE_PT`, 1e6 pt) at the
 * INDD import resolution (300 ppi). A non-finite offset or one past it — the wrap is dropped, as the importer drops it.
 */
export const TEXT_WRAP_MAX_OFFSET_PX = (1e6 * 300) / 72;

/** Zones one text frame keeps, the nearest to its top first. */
export const TEXT_WRAP_MAX_ZONES = 64;

const DEG = Math.PI / 180;

/** A point of `rect`'s own space (0..width, 0..height) on the page: the rect turns about its centre. */
function toPage(rect: RenderRect, [x, y]: Point): Point {
  const a = (rect.rotation ?? 0) * DEG;
  const dx = x - rect.width / 2;
  const dy = y - rect.height / 2;
  return [
    rect.x + rect.width / 2 + dx * Math.cos(a) - dy * Math.sin(a),
    rect.y + rect.height / 2 + dx * Math.sin(a) + dy * Math.cos(a),
  ];
}

/** A page point in `rect`'s own space. */
function toLocal(rect: RenderRect, [x, y]: Point): Point {
  const a = -(rect.rotation ?? 0) * DEG;
  const dx = x - (rect.x + rect.width / 2);
  const dy = y - (rect.y + rect.height / 2);
  return [rect.width / 2 + dx * Math.cos(a) - dy * Math.sin(a), rect.height / 2 + dx * Math.sin(a) + dy * Math.cos(a)];
}

function bounds(points: readonly Point[]): [number, number, number, number] {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const [x, y] of points) {
    x0 = Math.min(x0, x);
    y0 = Math.min(y0, y);
    x1 = Math.max(x1, x);
    y1 = Math.max(y1, y);
  }

  return [x0, y0, x1, y1];
}

/**
 * The zone `source` cuts out of the text frame `frame`, in the frame's own space. The rectangle is the source frame's
 * bounds there grown by the offsets (a turned source by its bounding box); a contour follows the source's polygon and
 * keeps `offsets[0]` from it.
 */
function zoneOf(wrap: BoxTextWrap, source: RenderRect, frame: RenderRect): WrapZone | undefined {
  // A wrap that is not of the Text Wrap shape (the first editor's boolean) or out of the importer's range wraps nothing.
  if (!isBoxTextWrap(wrap) || wrap.offsets.length < 4) return undefined;
  if (!wrap.offsets.every(o => Number.isFinite(o) && Math.abs(o) <= TEXT_WRAP_MAX_OFFSET_PX)) return undefined;
  const { width: w, height: h } = source;
  const map = (p: Point): Point => toLocal(frame, toPage(source, p));
  const outline = wrap.mode === 'contour' && wrap.contour && wrap.contour.length >= 3 ? thinned(wrap.contour) : undefined;
  const contour = outline?.map(([u, v]) => map([u * w, v * h]));
  if (contour) {
    const off = Math.max(wrap.offsets[0], 0);
    const [x0, y0, x1, y1] = bounds(contour);
    return {
      x0: x0 - off,
      y0: y0 - off,
      x1: x1 + off,
      y1: y1 + off,
      mode: 'contour',
      side: wrap.side,
      contour,
      contourOffset: off,
    };
  }

  const [top, left, bottom, right] = wrap.offsets.map(o => Math.max(o, 0));
  const [x0, y0, x1, y1] = bounds([map([0, 0]), map([w, 0]), map([w, h]), map([0, h])]);
  return {
    x0: x0 - left,
    y0: y0 - top,
    x1: x1 + right,
    y1: y1 + bottom,
    mode: wrap.mode === 'jump' ? 'jump' : 'box',
    side: wrap.side,
  };
}

/** `points` cut down to {@link TEXT_WRAP_MAX_CONTOUR_POINTS}, every n-th kept (as the importer thins its contour). */
function thinned(points: readonly Point[]): readonly Point[] {
  if (points.length <= TEXT_WRAP_MAX_CONTOUR_POINTS) return points;
  const step = Math.ceil(points.length / TEXT_WRAP_MAX_CONTOUR_POINTS);
  return points.filter((_, i) => i % step === 0);
}

/** The text can wrap around objects: a block of the paragraph layout, straight, unflipped, with no area contour or path. */
function wraps(t: RenderTextBox): boolean {
  return (
    t.textMode === 'block' &&
    !t.areaShape?.d &&
    !t.textPath?.d &&
    t.orientation !== 'vertical' &&
    !t.flipHorizontal &&
    !t.flipVertical &&
    !t.ignoreTextWrap &&
    textLayoutAtLeast(t.textLayout, TEXT_LAYOUT_PARAGRAPHS)
  );
}

/**
 * InDesign Text Wrap over the boxes of ONE page (page-local coordinates, before any collage shift): every visible box
 * with a `textWrap` cuts a zone out of each text frame of the page it reaches (`wrapZones`, in the frame's own space),
 * and the wrap fields are dropped — the engine reads only the zones. Every text of the page wraps whatever its order
 * (`Text Wrap Only Affects Text Beneath` is not modelled); a box never wraps itself, nor a group's wrap the texts in
 * the group (`textWrapGroups`, as InDesign); a text with `ignoreTextWrap`, or one the engine cannot set around objects,
 * stays as it is. Only a page with no `textWrap` on any box and no `ignoreTextWrap` or `textWrapGroups` on any text
 * comes back as the same array; any other page comes back as a copy with those fields dropped.
 */
export function resolveTextWraps(boxes: readonly RenderBox[]): RenderBox[] {
  if (!boxes.some(b => b.textWrap || (b.type === 'text' && (b.ignoreTextWrap || b.textWrapGroups)))) {
    return boxes as RenderBox[];
  }

  const sources = boxes.filter(b => isBoxTextWrap(b.textWrap) && b.visible !== false);
  return boxes.map((box): RenderBox => {
    const out = { ...box };
    delete out.textWrap;
    if (out.type !== 'text') return out;
    delete out.ignoreTextWrap;
    delete out.textWrapGroups;
    if (!wraps(box as RenderTextBox)) return out;
    const frame = out.rect;
    const groups = (box as RenderTextBox).textWrapGroups;
    const zones: WrapZone[] = [];
    for (const source of sources) {
      if (source === box || !source.textWrap) continue;
      if (source.textWrap.group && groups?.includes(source.textWrap.group)) continue;
      const zone = zoneOf(source.textWrap, source.rect, frame);
      // Only an object across the frame wraps its text (designcraft-compose `frame_wrap_exclusions`). Below the frame
      // still counts: a frame that grows as the text is typed sets its new lines around it (the engine skips a zone
      // no line reaches).
      if (zone && zone.x0 < frame.width && zone.x1 > 0 && zone.y1 > 0) zones.push(zone);
    }

    const kept = zones.length > TEXT_WRAP_MAX_ZONES ? zones.sort((a, b) => a.y0 - b.y0).slice(0, TEXT_WRAP_MAX_ZONES) : zones;
    if (kept.length > 0) out.wrapZones = kept;
    return out;
  });
}
